import { fCurrency } from 'src/utils/format-number';

// ----------------------------------------------------------------------

/**
 * The key holding the marketplace's own id for a listing. Shopify's regional stores all share one.
 */
export const platformIdKey = (platform) =>
  `${String(platform).includes('shopify') ? 'shopify' : platform}ListingId`;

/**
 * A name for one item under review that stays the same across a re-render.
 *
 * The marketplace's own id is the only thing that identifies the same item twice. A file import has
 * no marketplace id and carries the row id the import gave it instead. The position in the list is
 * the last resort, and it is a poor one — it stops meaning anything the moment a list is re-ordered.
 */
export function itemKey (listing, platform, index) {
  const id = listing?.[platformIdKey(platform)] ?? listing?._tempId ?? listing?.id;
  return id == null ? `row-${index}` : String(id);
}

/** The same name, for a match — a match is named after the item that is coming in. */
export function matchKey (match, platform, index) {
  return itemKey(match?.newListing, platform, index);
}

// ----------------------------------------------------------------------

/**
 * What a seller can decide about an item that looks like one they already have.
 *
 * The values are the words the server has always used and are sent on unchanged; the labels below
 * are the only place the seller's wording lives.
 */
export const DECISION = {
  /** Attach the marketplace post to the listing the seller already has. Nothing is overwritten. */
  LINK: 'merge',
  /** It is a different item after all — bring it in on its own. Costs a plan slot. */
  NEW_ITEM: 'separate',
  /** Don't bring it in at all. */
  LEAVE_OUT: 'skip',
};

/** `short` sits in the closed control, `long` in the open menu, `all` on the bulk button. */
export const DECISION_LABEL = {
  [DECISION.LINK]: { short: 'Link them', long: 'Link them (same item)', all: 'Link all' },
  [DECISION.NEW_ITEM]: { short: 'New item', long: 'Bring it in as a new item', all: null },
  [DECISION.LEAVE_OUT]: { short: 'Leave out', long: "Don't bring it in", all: 'Leave all out' },
};

/** "1 item", "3 items" — counts are always spelled out with the thing they count. */
export const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/**
 * Importing from a connected marketplace.
 *
 * The post can be attached to the listing the seller already has, so all three choices are real and
 * linking is what they nearly always want: it is the one choice that does not leave them with two
 * of the same item.
 */
export const MARKETPLACE_IMPORT = {
  choices: [DECISION.LINK, DECISION.NEW_ITEM, DECISION.LEAVE_OUT],
  defaultChoice: DECISION.LINK,
  heading: (count) =>
    count === 1
      ? '1 item looks like one you already have'
      : `${count} items look like ones you already have`,
  explainer: (marketplaceName) =>
    `We'll link each one to the listing you already have instead of making a second copy.${
      marketplaceName ? ` Nothing changes on ${marketplaceName}.` : ''
    }`,
};

/**
 * Importing from a file.
 *
 * A row in a file is not a post on a marketplace, so there is nothing to attach to anything —
 * linking is not on offer, and a row that matches something the seller already has is simply left
 * out unless they say otherwise.
 */
export const FILE_IMPORT = {
  choices: [DECISION.LEAVE_OUT, DECISION.NEW_ITEM],
  defaultChoice: DECISION.LEAVE_OUT,
  heading: (count) =>
    count === 1
      ? '1 row looks like a listing you already have'
      : `${count} rows look like listings you already have`,
  explainer: () =>
    "We'll leave them out so you don't get two of the same. Pick any you'd still like to bring in.",
};

// ----------------------------------------------------------------------

// A title this long already says which item it is; the rest would push the row's other facts off
// the end of it.
const MAX_TITLE_CHARS = 40;

const shorten = (text) =>
  text.length > MAX_TITLE_CHARS ? `${text.slice(0, MAX_TITLE_CHARS)}…` : text;

const sameText = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

/**
 * Which kind of match a reason describes.
 *
 * The reasons arrive as sentences written by whichever import found the match, and the two imports
 * word them differently ("Title exact match", "title"), so they are read for their subject rather
 * than compared exactly.
 */
function kindOf (reason) {
  const text = String(reason).toLowerCase();
  if (text.includes('sku')) return 'sku';
  if (text.includes('sold or relisted')) return 'history';
  if (text.includes('title')) return 'title';
  return null;
}

/**
 * Why this looks like something the seller already has, said in as few words as it takes.
 *
 * This is the whole point of the row. Two pictures side by side do not say why they are together —
 * the seller has to be told what we matched on — so it is kept apart from the rest of the row's
 * facts and shown as its own thing, rather than becoming another clause in a grey line.
 *
 * A match also arrives with a score, but every match gets the same score, so it says nothing and is
 * deliberately not read. The reasons are the only part that carries information.
 *
 * @param {object} match one entry of the review — `{ newListing, existingListing, matchReasons }`
 * @returns {{label: string, tone: 'sku'|'title'}} `tone` is which kind of match it is, for styling
 */
export function matchReason (match) {
  const kinds = new Set((match?.matchReasons || []).map(kindOf).filter(Boolean));

  if (kinds.has('sku')) {
    const sku = match?.newListing?.sku || match?.existingListing?.sku;
    return { label: sku ? `Same SKU ${sku}` : 'Same SKU', tone: 'sku' };
  }

  if (kinds.has('title')) return { label: 'Same title', tone: 'title' };

  return { label: 'Looks like the same item', tone: 'title' };
}

/**
 * What else the seller needs to tell the two items apart, beside the reason itself.
 *
 * @param {object} match
 * @param {string} [marketplaceName] the shop's name in the seller's words, for the history note
 * @returns {string}
 */
export function matchDetails (match, marketplaceName) {
  const incoming = match?.newListing || {};
  const existing = match?.existingListing || {};
  const kinds = new Set((match?.matchReasons || []).map(kindOf).filter(Boolean));

  const parts = [];

  // Two items can share a SKU and be nothing alike, so name the listing this would join. On a
  // title match that would only repeat the row's own title back at it.
  if (existing.title && !sameText(existing.title, incoming.title)) {
    parts.push(`Matches “${shorten(existing.title)}”`);
  }

  const price = fCurrency(incoming.price, incoming.currency);
  if (price) parts.push(price);

  if (kinds.has('history')) {
    parts.push(
      marketplaceName ? `you had this on ${marketplaceName} before` : 'you had this here before'
    );
  }

  return parts.join(' · ');
}

/**
 * The facts under a listing the seller is picking from: what tells it apart from its namesakes.
 */
export function listingSummary (listing) {
  const parts = [];
  if (listing?.sku) parts.push(`SKU ${listing.sku}`);
  const price = fCurrency(listing?.price, listing?.currency);
  if (price) parts.push(price);
  return parts.join(' · ');
}
